import React, { useState, useEffect } from 'react';
import { Phone, Mail, FileText, Globe, Printer, AlertCircle, CheckCircle, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { config } from '@/src/config';
import { useFormattedDate } from '../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';

interface CommunicationMethod {
  id: number;
  provider_id: string;
  provider_name: string;
  method_type: 'phone' | 'email' | 'web_form' | 'portal' | 'fax';
  contact_value: string;
  verified: boolean;
  verified_at?: string;
  success_count: number;
  failure_count: number;
  success_rate: number;
  last_success_at?: string;
  last_failure_at?: string;
  priority: number;
  is_preferred: boolean;
  discovery_method: string;
  discovered_at: string;
  source_url?: string;
  notes?: string;
  accepts_referrals: boolean;
  business_hours?: Record<string, string>;
  created_at: string;
  updated_at: string;
}

interface CommunicationMethodSelectorProps {
  providerId: string;
  providerName: string;
  referralId?: string;
  onMethodSelected?: (method: CommunicationMethod) => void;
  onSend?: (method: CommunicationMethod, message: string) => Promise<void>;
  fallbackContacts?: {
    phone?: string;
    email?: string;
    website?: string;
    address?: string;
  };
}

export function CommunicationMethodSelector({
  providerId,
  providerName,
  referralId,
  onMethodSelected,
  onSend,
  fallbackContacts
}: CommunicationMethodSelectorProps) {
  console.log('🚀 CommunicationMethodSelector mounted with:', { providerId, providerName, referralId });

  const { fmt } = useFormattedDate();
  const [methods, setMethods] = useState<CommunicationMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<CommunicationMethod | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  // Fetch communication methods
  useEffect(() => {
    console.log('⚡ useEffect triggered, calling fetchMethods for provider:', providerId);
    fetchMethods();
  }, [providerId]);

  const fetchMethods = async () => {
    try {
      console.log('🔍 CommunicationMethodSelector: Starting fetch for provider:', providerId);
      setLoading(true);
      setError(null);

      const url = `${config.api.baseUrl}/api/provider-communication-methods?provider_id=${providerId}`;
      console.log('📡 Fetching from URL:', url);

      const response = await authFetch(url);

      console.log('📥 Response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error('Failed to fetch communication methods');
      }

      const data = await response.json();

      console.log('📞 Communication methods fetched:', data);
      console.log('📋 Fallback contacts available:', fallbackContacts);

      // If no methods found and fallback contacts available, create fallback methods
      if (data.length === 0 && fallbackContacts) {
        console.log('🔄 Creating fallback methods from provider directory...');
        const fallbackMethods: any[] = [];

        if (fallbackContacts.phone) {
          fallbackMethods.push({
            id: -1,
            provider_id: providerId,
            provider_name: providerName,
            method_type: 'phone',
            contact_value: fallbackContacts.phone,
            verified: false,
            success_count: 0,
            failure_count: 0,
            success_rate: 0,
            priority: 60,
            is_preferred: false,
            discovery_method: 'fallback',
            discovered_at: new Date().toISOString(),
            accepts_referrals: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }

        if (fallbackContacts.email) {
          fallbackMethods.push({
            id: -2,
            provider_id: providerId,
            provider_name: providerName,
            method_type: 'email',
            contact_value: fallbackContacts.email,
            verified: false,
            success_count: 0,
            failure_count: 0,
            success_rate: 0,
            priority: 80,
            is_preferred: false,
            discovery_method: 'fallback',
            discovered_at: new Date().toISOString(),
            accepts_referrals: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }

        if (fallbackContacts.website) {
          // Ensure website has proper protocol to avoid weavein.ai prefix
          const websiteUrl = fallbackContacts.website.startsWith('http')
            ? fallbackContacts.website
            : `https://${fallbackContacts.website}`;

          fallbackMethods.push({
            id: -3,
            provider_id: providerId,
            provider_name: providerName,
            method_type: 'web_form',
            contact_value: websiteUrl,
            verified: false,
            success_count: 0,
            failure_count: 0,
            success_rate: 0,
            priority: 100,
            is_preferred: false,
            discovery_method: 'fallback',
            discovered_at: new Date().toISOString(),
            accepts_referrals: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }

        const sortedFallbackMethods = fallbackMethods.sort((a, b) => b.priority - a.priority);
        console.log('✅ Created fallback methods:', sortedFallbackMethods);
        setMethods(sortedFallbackMethods);

        if (sortedFallbackMethods.length > 0) {
          setSelectedMethod(sortedFallbackMethods[0]);
          onMethodSelected?.(sortedFallbackMethods[0]);
        } else {
          console.log('⚠️ No fallback methods created - check if fallbackContacts has valid data');
        }
      } else {
        setMethods(data);

        // Auto-select highest priority method
        if (data.length > 0 && !selectedMethod) {
          setSelectedMethod(data[0]);
          onMethodSelected?.(data[0]);
        }
      }
    } catch (err) {
      console.error('❌ Error fetching communication methods:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const triggerDiscovery = async () => {
    try {
      setDiscovering(true);
      setError(null);

      const response = await authFetch(`${config.api.baseUrl}/api/provider-communication-methods/discover/${providerId}`);

      if (!response.ok) {
        throw new Error('Failed to discover contacts');
      }

      const result = await response.json();

      if (result.success) {
        // Refresh methods - discovery found contacts
        await fetchMethods();
      } else {
        // Discovery failed but don't show error - just refresh which will use fallback
        console.log('⚠️ Discovery failed:', result.error);
        console.log('🔄 Using fallback contacts from Provider Directory');
        await fetchMethods();
      }
    } catch (err) {
      console.error('❌ Discovery error:', err);
      // Don't show error to user - just use fallback
      await fetchMethods();
    } finally {
      setDiscovering(false);
    }
  };

  const handleMethodSelect = (method: CommunicationMethod) => {
    setSelectedMethod(method);
    onMethodSelected?.(method);
  };

  const handleSend = async () => {
    if (!selectedMethod || !message.trim()) return;

    try {
      setSending(true);

      if (onSend) {
        await onSend(selectedMethod, message);
      } else {
        // Default: Create communication attempt and send via email/fax/etc.
        const response = await authFetch(`${config.api.baseUrl}/api/referral-communications`, {
          method: 'POST',
          jsonBody: {
            referral_id: referralId,
            provider_id: providerId,
            provider_name: providerName,  // Pass provider name for email/fax templates
            communication_method_id: selectedMethod.id,
            method_type: selectedMethod.method_type,
            contact_value: selectedMethod.contact_value,
            message_content: message,
            status: 'sent',
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to send communication');
        }

        const result = await response.json();
        console.log('✅ Communication sent:', result);
      }

      setMessage('');
      alert('Communication sent successfully!');

      // Track success
      await trackCommunicationResult(selectedMethod.id, true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');

      // Track failure
      if (selectedMethod) {
        await trackCommunicationResult(selectedMethod.id, false);
      }
    } finally {
      setSending(false);
    }
  };

  const trackCommunicationResult = async (methodId: number, success: boolean) => {
    // Skip tracking for fallback methods (negative IDs are not stored in database)
    if (methodId < 0) {
      console.log('⏭️ Skipping tracking for fallback method:', methodId);
      return;
    }

    try {
      const response = await authFetch(`${config.api.baseUrl}/api/provider-communication-methods/${methodId}`, {
        method: 'PUT',
        jsonBody: { success }
      });

      if (response.ok) {
        // Refresh methods to show updated priority
        await fetchMethods();
      }
    } catch (err) {
      console.error('Failed to track communication result:', err);
    }
  };

  // Message templates for different communication methods
  const getTemplates = (methodType: string) => {
    if (methodType === 'phone') {
      return [
        {
          name: 'SMS - Brief Referral',
          content: `Hi ${providerName}, this is ${localStorage.getItem('user_name') || 'a care coordinator'} from Trust Marketplace. I have a referral for you. Please call me back at your earliest convenience to discuss. Thank you!`
        },
        {
          name: 'SMS - Detailed Referral',
          content: `Hello ${providerName},\n\nI'm referring a client who needs assistance with housing and food. They are located in the ${referralId ? 'area' : 'local area'} and would benefit from your services.\n\nCan we schedule a time to discuss this referral?\n\nBest regards,\n${localStorage.getItem('user_name') || 'Care Coordinator'}`
        },
        {
          name: 'SMS - Urgent Referral',
          content: `URGENT: Hi ${providerName}, I have an urgent referral that needs immediate attention. Please contact me as soon as possible. Contact: ${localStorage.getItem('user_phone') || 'See portal'}`
        }
      ];
    } else if (methodType === 'email') {
      return [
        {
          name: 'Email - Standard Referral',
          content: `Dear ${providerName},\n\nI hope this message finds you well. I am writing to refer a client who would benefit from your services.\n\nClient needs include:\n- Housing assistance\n- Food security\n- Additional support services\n\nPlease let me know if you can accept this referral and what information you need from us.\n\nBest regards,\n${localStorage.getItem('user_name') || 'Care Coordinator'}\n${localStorage.getItem('user_email') || ''}`
        },
        {
          name: 'Email - Detailed with Follow-up',
          content: `Dear ${providerName},\n\nI am reaching out to refer a client who matches your service offerings.\n\nReferral Details:\n- Services Needed: Housing, Food assistance\n- Urgency: Standard\n- Follow-up: I will call within 48 hours to discuss\n\nPlease confirm receipt of this referral at your earliest convenience.\n\nThank you for your partnership,\n${localStorage.getItem('user_name') || 'Care Coordinator'}`
        }
      ];
    } else if (methodType === 'fax') {
      return [
        {
          name: 'Fax - Formal Referral',
          content: `TO: ${providerName}\nFROM: ${localStorage.getItem('user_name') || 'Care Coordinator'}\nDATE: ${fmt.short(new Date())}\nRE: Client Referral\n\n--------------------\n\nPlease accept this referral for a client in need of your services.\n\nClient Requirements:\n- Services: Housing, Food assistance\n- Location: Local area\n- Contact: See attached documentation\n\nPlease fax confirmation of receipt to our office.\n\nThank you,\n${localStorage.getItem('user_name') || 'Care Coordinator'}`
        }
      ];
    }
    return [];
  };

  const applyTemplate = (template: { name: string; content: string }) => {
    setMessage(template.content);
    setShowTemplates(false);
  };

  const handleVerify = async (methodId: number) => {
    // Skip for fallback methods (negative IDs are not stored in database)
    if (methodId < 0) {
      alert('Fallback contact methods cannot be verified. Add a new method from the provider directory to track it.');
      return;
    }

    try {
      const response = await authFetch(`${config.api.baseUrl}/api/provider-communication-methods/${methodId}`, {
        method: 'PUT',
        jsonBody: { verified: true }
      });

      if (response.ok) {
        // Refresh methods to show updated verification
        await fetchMethods();
        alert('Communication method verified successfully!');
      } else {
        throw new Error('Failed to verify method');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const handleSetPreferred = async (methodId: number) => {
    // Skip for fallback methods (negative IDs are not stored in database)
    if (methodId < 0) {
      alert('Fallback contact methods cannot be set as preferred. Add a new method from the provider directory to track it.');
      return;
    }

    try {
      const response = await authFetch(`${config.api.baseUrl}/api/provider-communication-methods/${methodId}`, {
        method: 'PUT',
        jsonBody: { is_preferred: true }
      });

      if (response.ok) {
        // Refresh methods to show updated preference
        await fetchMethods();
      } else {
        throw new Error('Failed to set preferred method');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const getMethodIcon = (type: string) => {
    switch (type) {
      case 'phone': return <Phone className="w-5 h-5" />;
      case 'email': return <Mail className="w-5 h-5" />;
      case 'web_form': return <FileText className="w-5 h-5" />;
      case 'portal': return <Globe className="w-5 h-5" />;
      case 'fax': return <Printer className="w-5 h-5" />;
      default: return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getMethodLabel = (type: string) => {
    switch (type) {
      case 'phone': return 'SMS / Phone';
      case 'email': return 'Email';
      case 'web_form': return 'Web Form';
      case 'portal': return 'Provider Portal';
      case 'fax': return 'Fax';
      default: return type;
    }
  };

  const getScoreColor = (priority: number) => {
    if (priority >= 90) return 'text-green-600';
    if (priority >= 70) return 'text-blue-600';
    if (priority >= 50) return 'text-yellow-600';
    return 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading communication methods...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Send Referral to {providerName}
        </h3>
        {methods.length === 0 && (
          <Button
            onClick={triggerDiscovery}
            disabled={discovering}
            variant="outline"
            size="sm"
          >
            {discovering ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Discovering...
              </>
            ) : (
              'Discover Contacts'
            )}
          </Button>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {methods.length === 0 ? (
        <div className="p-8 bg-gray-50 rounded-lg text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h4 className="text-sm font-medium text-gray-900 mb-1">
            No contact methods available
          </h4>
          <p className="text-sm text-gray-600">
            {fallbackContacts?.phone || fallbackContacts?.email || fallbackContacts?.website ? (
              'Click "Discover Contacts" to automatically find contact information'
            ) : (
              'No contact information found for this provider in the directory. Please add provider contact details to enable referrals.'
            )}
          </p>
        </div>
      ) : (
        <>
          {/* Communication Methods List */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Select Communication Method
            </label>
            <div className="space-y-2">
              {methods.map((method) => (
                <Card
                  key={method.id}
                  className={`p-4 cursor-pointer transition-all ${
                    selectedMethod?.id === method.id
                      ? 'border-blue-500 bg-blue-50 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleMethodSelect(method)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="text-gray-700 mt-1">
                        {getMethodIcon(method.method_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">
                            {getMethodLabel(method.method_type)}
                          </span>
                          {method.verified && (
                            <CheckCircle className="w-4 h-4 text-green-600" title="Verified" />
                          )}
                          {method.is_preferred && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                              Preferred
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 break-all">
                          {method.contact_value}
                        </p>
                        {method.notes && (
                          <p className="text-xs text-gray-500 mt-1">{method.notes}</p>
                        )}
                        {method.success_count + method.failure_count > 0 && (
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                            <span>
                              Success: {method.success_count}/{method.success_count + method.failure_count}
                            </span>
                            <span className="text-gray-400">•</span>
                            <span>Rate: {method.success_rate}%</span>
                          </div>
                        )}

                        {/* Action Buttons - Only show for discovered methods, not fallback */}
                        {method.id > 0 && (
                          <div className="flex items-center space-x-2 mt-3" onClick={(e) => e.stopPropagation()}>
                            {!method.verified && (
                              <Button
                                onClick={() => handleVerify(method.id)}
                                variant="outline"
                                size="sm"
                                className="text-xs"
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Verify
                              </Button>
                            )}
                            {!method.is_preferred && (
                              <Button
                                onClick={() => handleSetPreferred(method.id)}
                                variant="outline"
                                size="sm"
                                className="text-xs"
                              >
                                Set Preferred
                              </Button>
                            )}
                          </div>
                        )}
                        {/* Show fallback indicator for Provider Directory contacts */}
                        {method.id < 0 && (
                          <div className="mt-2">
                            <span className="text-xs text-gray-500 italic">
                              From Provider Directory
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex flex-col items-end space-y-1">
                      <span className={`text-2xl font-bold ${getScoreColor(method.priority)}`}>
                        {method.priority}
                      </span>
                      <span className="text-xs text-gray-500">Priority</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Message Input */}
          {selectedMethod && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  Referral Message
                </label>
                {(selectedMethod.method_type === 'phone' || selectedMethod.method_type === 'email' || selectedMethod.method_type === 'fax') && (
                  <Button
                    onClick={() => setShowTemplates(!showTemplates)}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    {showTemplates ? 'Hide Templates' : 'Use Template'}
                  </Button>
                )}
              </div>

              {/* Show prominent phone number for SMS */}
              {selectedMethod.method_type === 'phone' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-blue-900 mb-1">Sending SMS to:</p>
                      <p className="text-lg font-bold text-blue-700">{selectedMethod.contact_value}</p>
                    </div>
                    <Phone className="w-8 h-8 text-blue-500" />
                  </div>
                </div>
              )}

              {/* Template Selector */}
              {showTemplates && (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
                  <p className="text-xs font-medium text-gray-700 mb-2">Choose a template:</p>
                  {getTemplates(selectedMethod.method_type).map((template, index) => (
                    <button
                      key={index}
                      onClick={() => applyTemplate(template)}
                      className="w-full text-left p-2 bg-white border border-gray-200 rounded hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    >
                      <p className="text-sm font-medium text-gray-900">{template.name}</p>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{template.content}</p>
                    </button>
                  ))}
                </div>
              )}

              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={`Enter your referral message for ${providerName}...`}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={selectedMethod.method_type === 'phone' ? 6 : 4}
              />
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-3">
                  {selectedMethod.method_type === 'web_form' && 'This will submit via the provider\'s web form'}
                  {selectedMethod.method_type === 'portal' && 'This will be sent through the provider portal'}
                  {selectedMethod.method_type === 'email' && 'This will be sent as an email'}
                  {selectedMethod.method_type === 'fax' && 'This will be sent as a fax'}
                  {selectedMethod.method_type === 'phone' && `This will send an SMS text message to ${selectedMethod.contact_value}`}
                </p>
                <Button
                  onClick={handleSend}
                  disabled={sending || !message.trim()}
                  className="w-full bg-v2accent hover:bg-v2accent/90 text-white font-semibold py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Referral'
                  )}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Ranking Explanation */}
      {methods.length > 0 && (
        <div className="p-3 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Smart Priority Ranking</h4>
          <ul className="text-xs text-gray-600 space-y-1">
            <li>• <strong>Base Score:</strong> Web Form (100), Portal (90), Email (80), Fax (70), Phone (60)</li>
            <li>• <strong>Verification Bonus:</strong> +20 points for verified methods</li>
            <li>• <strong>Success Rate Bonus:</strong> Up to +30 points based on delivery success</li>
            <li>• <strong>Recency Bonus:</strong> +10 points if used in last 30 days, +5 if last 90 days</li>
            <li>• <strong>Preferred Bonus:</strong> +5 points for provider's preferred method</li>
            <li>• Priority auto-updates after each communication attempt</li>
          </ul>
        </div>
      )}
    </div>
  );
}

export default CommunicationMethodSelector;
